import Button from "@mui/material/Button";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import useTheme from "@mui/material/styles/useTheme";

import { neutral } from "app/themes/colors";

import BasePicker from "./base-picker.component";

/** The columns (205px) plus the OK button hanging under them. */
export const TIME_PICKER_DROPDOWN_HEIGHT = 245;

/**
 * Open upward when the dropdown would run past the bottom of the window and there is room
 * above: on a laptop-height screen a field low in a dialog put OK below the fold (ENG-2128).
 */
export const dropdownOpensAbove = (
  field: { top: number; bottom: number },
  viewportHeight: number
): boolean =>
  field.bottom + TIME_PICKER_DROPDOWN_HEIGHT > viewportHeight &&
  field.top > TIME_PICKER_DROPDOWN_HEIGHT;

type TimePickerDropdownProps = {
  hour: string;
  minute: string;
  amPm: string;
  hours: string[];
  minutes: string[];
  amPms: string[];
  onHour: (value: string) => void;
  onMinute: (value: string) => void;
  onAmPm: (value: string) => void;
  /** OK, or a double-click on any column. */
  onClose: () => void;
  onClickAway: (event: MouseEvent | TouchEvent) => void;
  /** The field above has a floating label, so the dropdown sits lower. */
  hasLabel?: boolean;
  /**
   * Draw above the field instead of below. Needs the caller's root to be `position:
   * relative`. Off by default, so existing callers are unchanged.
   */
  above?: boolean;
};

/**
 * The hour / minute / am-pm columns and OK button under a time field. Lifted out of
 * `TimePicker` unchanged so a field that does not hold a react-hook-form `Date` (Scheduling
 * V3 keeps minutes) opens the same dropdown instead of a look-alike. It owns no state: the
 * caller decides when it is open and what a click-away means, because only the caller can
 * tell "outside the whole control" from "on my own field".
 */
export default function TimePickerDropdown({
  hour,
  minute,
  amPm,
  hours,
  minutes,
  amPms,
  onHour,
  onMinute,
  onAmPm,
  onClose,
  onClickAway,
  hasLabel = false,
  above = false
}: TimePickerDropdownProps) {
  const { palette } = useTheme();
  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <Paper
        sx={{
          flexDirection: "row",
          display: "flex",
          ...(above
            ? { bottom: `calc(100% + ${TIME_PICKER_DROPDOWN_HEIGHT - 205}px)` }
            : { transform: `translate(0,${hasLabel ? 60 : 32}px)` }),
          // `timePicker` is a sunoTheme palette key; a bare theme (unit tests) falls back.
          background: palette.timePicker?.bgcolor ?? palette.background.paper,
          position: "absolute",
          height: 205,
          width: 200,
          border: `solid 1px ${neutral[500]}`,
          zIndex: 2
        }}
      >
        <Stack direction="row">
          <BasePicker
            value={hour}
            options={hours}
            onClick={onHour}
            onDoubleClick={onClose}
          />
          <BasePicker
            value={minute}
            options={minutes}
            onClick={onMinute}
            onDoubleClick={onClose}
          />
          <BasePicker
            value={amPm}
            options={amPms}
            onClick={onAmPm}
            onDoubleClick={onClose}
            sx={{ overflowY: "hidden" }}
          />
        </Stack>
        <Button
          variant="contained"
          size="small"
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 207,
            right: 0,
            zIndex: 2,
            width: 203
          }}
        >
          OK
        </Button>
      </Paper>
    </ClickAwayListener>
  );
}
